import { aed, money2 } from "@/lib/format";
import { printBillReceipt } from "@/lib/billPrint";
import {
  buildReportDocument,
  openReportPrint,
  periodMeta,
  resolveReportLetterhead,
  escapeReportHtml,
} from "@/lib/reportPrintShell";

function cell(value) {
  return value != null && value !== "" ? aed(value) : "";
}

function esc(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function amt(value) {
  return money2(value);
}

/** yyyy-mm-dd → dd/mm/yyyy */
function fmtReportDate(iso) {
  const raw = String(iso || "").slice(0, 10);
  const [y, m, d] = raw.split("-");
  if (y && m && d) return `${d}/${m}/${y}`;
  return raw || "";
}

function fmtPrintDateTime(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) {
    return fmtPrintDateTime(new Date());
  }
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const time = d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
  return `${dd}/${mm}/${yyyy} ${time}`;
}

function resolveReportPrintAt(report, options = {}) {
  const candidates = [
    options.printAt,
    report?.day_end?.closed_at,
    report?.generated_at,
    report?.printed_at,
  ];
  for (const raw of candidates) {
    if (!raw) continue;
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return new Date();
}

function row(label, value) {
  return `<div class="row"><span class="lbl">${esc(label)}</span><span class="val">${esc(value)}</span></div>`;
}

function sectionTitle(title) {
  return `<div class="dash"></div><div class="sec">${esc(title)}</div><div class="dash"></div>`;
}

function doubleRule() {
  return `<div class="eq"></div>`;
}

async function buildHtml(title, report, metaHtml, tableHtml) {
  const company = await resolveReportLetterhead({
    branchId: report?.branch_id,
    allBranches: report?.all_branches,
  });
  return buildReportDocument({
    title,
    company,
    metaHtml,
    bodyHtml: tableHtml,
  });
}

function paymentAmount(report, method) {
  return Number(
    (report?.payments || []).find((p) => p.payment_method === method)?.amount ?? 0,
  ) || 0;
}

/**
 * Shared fields for restaurant Z/X thermal + A4 prints.
 */
export function buildZReportContext(report, options = {}) {
  if (!report) return null;

  const sales = report.sales || {};
  const cs = report.cash_summary || {};
  const denom = report.denomination || {};
  const settlement = report.settlement || {};

  const cash = Number(settlement.cash ?? cs.cash_received ?? paymentAmount(report, "cash")) || 0;
  const card = Number(settlement.card ?? paymentAmount(report, "card")) || 0;
  const credit = Number(settlement.credit ?? paymentAmount(report, "credit")) || 0;
  const otherPayments = (report.payments || []).filter(
    (p) => !["cash", "card", "credit"].includes(String(p.payment_method || "").toLowerCase()),
  );
  const other = otherPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const totalSales = Number(sales.net_sales ?? cash + card + credit + other) || 0;
  const opening = Number(cs.opening_float ?? 0) || 0;
  const cashRefunds = Number(settlement.cashOut ?? cs.cash_refunds ?? 0) || 0;
  const changeGiven = Number(cs.change_given ?? 0) || 0;
  const cashIn = Number(cs.cash_in ?? 0) || 0;
  const cashOut = Number(cs.cash_out ?? cashRefunds) || 0;
  const expectedCash = Number(settlement.expectedCash ?? cs.expected_cash ?? cash) || 0;
  const taxable = Number(sales.taxable_amount ?? 0) || 0;
  const vat = Number(sales.vat_collected ?? 0) || 0;
  const discount = Number(sales.discount ?? 0) || 0;
  const roundOff = Number(sales.round_off ?? 0) || 0;
  const billCount = Number(sales.bill_count ?? 0) || 0;

  const declaredTotal = Number(denom.declared_total ?? report.day_end?.counted_cash ?? 0) || 0;
  const cashDifference =
    denom.variance != null && denom.variance !== ""
      ? Number(denom.variance) || 0
      : declaredTotal
        ? Math.round((declaredTotal - expectedCash) * 100) / 100
        : Number(report.day_end?.difference ?? settlement.variance ?? 0) || 0;

  const byOrderType = Array.isArray(report.by_order_type) ? report.by_order_type : [];
  const denomLines = Array.isArray(denom.lines)
    ? denom.lines.filter((l) => Number(l.qty) > 0)
    : [];

  const title = options.title || (report.report === "x_report" ? "X-Report" : "Z-Report");
  const reportDate = fmtReportDate(report.date);
  const voucherNo = report.day_end?.voucher_no ?? report.day_end?.id ?? "";
  const cashier = String(
    options.cashier || report.cashier_name || report.cashier || "ADMIN",
  ).toUpperCase();
  const counterName = String(report.counter?.name || options.counterName || "—");
  const printAtLabel = fmtPrintDateTime(resolveReportPrintAt(report, options));

  return {
    title,
    reportDate,
    voucherNo,
    cashier,
    counterName,
    cash,
    card,
    credit,
    other,
    otherPayments,
    totalSales,
    opening,
    cashRefunds,
    changeGiven,
    cashIn,
    cashOut,
    expectedCash,
    taxable,
    vat,
    discount,
    roundOff,
    billCount,
    declaredTotal,
    cashDifference,
    byOrderType,
    denomLines,
    printAtLabel,
    payments: report.payments || [],
  };
}

function buildThermalZReportHtml(ctx, letterhead) {
  const companyName = letterhead?.name || letterhead?.company_name || "Restaurant";
  const addressLines = String(letterhead?.address || "")
    .split(/[,|\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (letterhead?.phone) addressLines.push(`Tel: ${letterhead.phone}`);
  if (letterhead?.tax_number) addressLines.push(`TRN: ${letterhead.tax_number}`);

  const paymentRows = [
    ["Cash sales", ctx.cash],
    ["Card sales", ctx.card],
    ...(ctx.credit > 0 ? [["Credit sales", ctx.credit]] : []),
    ...ctx.otherPayments.map((p) => [p.payment_method_label || p.payment_method, Number(p.amount) || 0]),
    ["Total sales", ctx.totalSales],
    ...(ctx.discount > 0 ? [["Discount", ctx.discount]] : []),
    ...(ctx.roundOff !== 0 ? [["Round off", ctx.roundOff]] : []),
    ...(ctx.cashRefunds > 0 ? [["Cash refunds", ctx.cashRefunds]] : []),
    ...(ctx.changeGiven > 0 ? [["Change given", ctx.changeGiven]] : []),
    ...(ctx.cashIn > 0 ? [["Cash in", ctx.cashIn]] : []),
    ...(ctx.cashOut > 0 && ctx.cashOut !== ctx.cashRefunds ? [["Cash out", ctx.cashOut]] : []),
    ["Expected cash", ctx.expectedCash],
    ...(ctx.opening > 0 ? [["Opening float", ctx.opening]] : []),
  ]
    .map(([l, v]) => row(String(l).toUpperCase(), amt(v)))
    .join("");

  const orderTypeRows =
    ctx.byOrderType.length > 0
      ? ctx.byOrderType
          .map(
            (r) =>
              row(
                String(r.order_type_label || r.order_type || "—").toUpperCase(),
                `${amt(r.net_sales)} (${Number(r.bill_count) || 0})`,
              ),
          )
          .join("")
      : `<div class="meta-line">None</div>`;

  const denomRows =
    ctx.denomLines.length > 0
      ? ctx.denomLines
          .map((l) => {
            const qty = Number(l.qty) || 0;
            const value = Number(l.value) || 0;
            const label = String(l.label || value || "").toUpperCase();
            return row(`${label} x${qty}`, amt(qty * value));
          })
          .join("") +
        row("COUNTED CASH", amt(ctx.declaredTotal)) +
        row("VARIANCE", amt(ctx.cashDifference))
      : ctx.declaredTotal > 0
        ? row("COUNTED CASH", amt(ctx.declaredTotal)) + row("VARIANCE", amt(ctx.cashDifference))
        : `<div class="meta-line">None</div>`;

  const reportLabel = ctx.title.toUpperCase().includes("X") ? "X Report" : "Z Report";

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${esc(ctx.title)}</title>
<style>
  @page { margin: 0; size: 80mm auto; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    color: #000 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  body {
    font-family: Consolas, "Courier New", monospace;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.28;
  }
  .wrap {
    width: 100%;
    max-width: 70mm;
    margin: 0 auto;
    padding: 2mm 2mm 28mm;
  }
  .center { text-align: center; }
  .shop { font-size: 13px; font-weight: 700; margin: 0 0 2px; text-transform: uppercase; }
  .addr { font-size: 10px; font-weight: 600; line-height: 1.25; }
  .meta-line { margin: 1px 0; font-size: 11px; }
  .row {
    display: flex;
    justify-content: space-between;
    gap: 6px;
    margin: 1px 0;
    font-size: 11px;
  }
  .row .lbl { flex: 1; min-width: 0; text-transform: uppercase; }
  .row .val { flex: 0 0 auto; text-align: right; white-space: nowrap; }
  .sec {
    text-align: center;
    font-weight: 700;
    font-size: 11px;
    letter-spacing: 0.02em;
    padding: 2px 0;
    text-transform: uppercase;
  }
  .eq, .dash {
    overflow: hidden;
    white-space: nowrap;
    font-size: 10px;
    line-height: 1;
    margin: 4px 0;
    letter-spacing: -0.5px;
  }
  .eq::before { content: "========================================"; }
  .dash::before { content: "----------------------------------------"; }
  .footer-line { margin-top: 6px; font-size: 10px; }
  .end { text-align: center; margin-top: 8px; font-weight: 700; }
  .tail { height: 16mm; }
</style>
</head>
<body>
<div class="wrap">
  <div class="center shop">${esc(companyName)}</div>
  ${addressLines.map((l) => `<div class="center addr">${esc(l)}</div>`).join("")}
  <div class="meta-line">${esc(reportLabel)} Date: ${esc(ctx.reportDate)}</div>
  <div class="meta-line">Counter : ${esc(ctx.counterName)}</div>
  ${ctx.voucherNo !== "" && ctx.voucherNo != null
    ? `<div class="meta-line">Voucher No : ${esc(String(ctx.voucherNo))}</div>`
    : ""}
  ${doubleRule()}
  <div class="meta-line">CASHIER : ${esc(ctx.cashier)}</div>
  <div class="meta-line">BILLS : ${esc(String(ctx.billCount))}</div>
  ${doubleRule()}
  ${sectionTitle(`${reportLabel.toUpperCase()} SUMMARY`)}
  ${paymentRows}
  ${doubleRule()}
  ${sectionTitle("TAX SUMMARY")}
  ${row("TAXABLE AMOUNT", amt(ctx.taxable))}
  ${row("OUTPUT VAT", amt(ctx.vat))}
  ${row("DISCOUNT", amt(ctx.discount))}
  ${doubleRule()}
  ${sectionTitle("ORDER TYPE")}
  ${orderTypeRows}
  ${doubleRule()}
  ${sectionTitle("DENOMINATION / CASH COUNT")}
  ${denomRows}
  ${doubleRule()}
  <div class="footer-line">Print Date&amp;Time: ${esc(ctx.printAtLabel)}</div>
  <div class="end">xxxxx END xxxxx</div>
  <div class="tail" aria-hidden="true"></div>
</div>
</body>
</html>`;
}

export async function printZReportThermal(report, options = {}) {
  const ctx = buildZReportContext(report, options);
  if (!ctx) return false;
  const letterhead = await resolveReportLetterhead({
    branchId: report?.branch_id ?? options.branchId,
    allBranches: report?.all_branches ?? options.allBranches,
  });
  const html = buildThermalZReportHtml(ctx, letterhead);
  return printBillReceipt({ html, paper_width_mm: 80 }, ctx.title);
}

export async function printCashCollectionReport(report) {
  if (!report) return false;

  const methodRows = (report.summary?.by_method || [])
    .map(
      (r) => `
      <tr>
        <td>${escapeReportHtml(r.payment_method_label)}</td>
        <td class="num">${r.bill_count}</td>
        <td class="num">${cell(r.amount)}</td>
      </tr>`,
    )
    .join("");

  const html = await buildHtml(
    "Cash Collection Report",
    report,
    `${periodMeta(report)}
     <div><strong>Round off:</strong> ${cell(report.summary?.round_off)}</div>
     <div><strong>Total collected:</strong> ${cell(report.summary?.total_collected)}</div>`,
    `<table>
      <thead><tr><th>Method</th><th>Bills</th><th>Amount</th></tr></thead>
      <tbody>${methodRows}</tbody>
    </table>`,
  );

  return openReportPrint(html);
}

export async function printZReportA4(report, title = "Z-Report") {
  if (!report) return false;

  const paymentRows = (report.payments || [])
    .map(
      (p) => `
      <tr>
        <td>${escapeReportHtml(p.payment_method_label)}</td>
        <td class="num">${p.bill_count}</td>
        <td class="num">${cell(p.amount)}</td>
        <td class="num">${cell(p.change_amount)}</td>
      </tr>`,
    )
    .join("");

  const denomRows = (report.denomination?.lines || [])
    .filter((d) => d.qty > 0)
    .map(
      (d) => `
      <tr>
        <td class="num">${escapeReportHtml(d.label)}</td>
        <td class="num">${d.qty}</td>
        <td class="num">${cell(d.amount)}</td>
      </tr>`,
    )
    .join("");

  const orderTypeRows = (report.by_order_type || [])
    .map(
      (r) => `
      <tr>
        <td>${escapeReportHtml(r.order_type_label || r.order_type)}</td>
        <td class="num">${r.bill_count}</td>
        <td class="num">${cell(r.net_sales)}</td>
      </tr>`,
    )
    .join("");

  const html = await buildHtml(
    title,
    report,
    `<div><strong>Counter:</strong> ${escapeReportHtml(report.counter?.name)}</div>
     <div><strong>Date:</strong> ${escapeReportHtml(report.date)}</div>
     <div><strong>Round off:</strong> ${cell(report.sales?.round_off)}</div>
     <div><strong>Net sales:</strong> ${cell(report.sales?.net_sales)}</div>
     <div><strong>Expected cash:</strong> ${cell(report.cash_summary?.expected_cash)}</div>
     ${report.day_end?.counted_cash != null
       ? `<div><strong>Counted cash:</strong> ${cell(report.day_end.counted_cash)}</div>`
       : ""}`,
    `<h2>Payments</h2>
    <table>
      <thead><tr><th>Method</th><th>Bills</th><th>Amount</th><th>Change</th></tr></thead>
      <tbody>${paymentRows}</tbody>
    </table>
    ${orderTypeRows ? `<h2>Order type</h2>
    <table>
      <thead><tr><th>Type</th><th>Bills</th><th>Net</th></tr></thead>
      <tbody>${orderTypeRows}</tbody>
    </table>` : ""}
    ${denomRows ? `<h2>Denomination</h2>
    <table>
      <thead><tr><th>Value</th><th>Qty</th><th>Amount</th></tr></thead>
      <tbody>${denomRows}</tbody>
    </table>` : ""}`,
  );

  return openReportPrint(html);
}

/**
 * @param {object} report
 * @param {{ cashier?: string, title?: string, format?: 'thermal'|'a4', branchId?: number, allBranches?: boolean }} [options]
 */
export async function printZReport(report, options = {}) {
  const format = options.format === "a4" ? "a4" : "thermal";
  const title = options.title || "Z-Report";
  if (format === "a4") {
    return printZReportA4(report, title);
  }
  return printZReportThermal(report, options);
}

export async function printSalesDayBook(report) {
  if (!report) return false;

  const entryRows = (report.entries || [])
    .map(
      (e) => `
      <tr>
        <td class="mono">${escapeReportHtml(e.bill_number)}</td>
        <td>${escapeReportHtml(e.paid_time || "")}</td>
        <td>${escapeReportHtml(e.order_type_label)}</td>
        <td>${escapeReportHtml(e.counter_name || "")}</td>
        <td>${escapeReportHtml(e.customer_name || "")}</td>
        <td class="mono">${escapeReportHtml(e.customer_tax_number || "")}</td>
        <td class="num">${cell(e.round_off)}</td>
        <td class="num">${cell(e.net_amount)}</td>
      </tr>`,
    )
    .join("");

  const html = await buildHtml(
    "Sales Day Book",
    report,
    `<div><strong>Date:</strong> ${escapeReportHtml(report.date)}</div>
     <div><strong>Bills:</strong> ${report.summary?.bill_count ?? 0}</div>
     <div><strong>Round off:</strong> ${cell(report.summary?.round_off)}</div>
     <div><strong>Net sales:</strong> ${cell(report.summary?.net_sales)}</div>`,
    `<table>
      <thead><tr><th>Bill</th><th>Time</th><th>Type</th><th>Counter</th><th>Customer</th><th>TRN</th><th>Round off</th><th>Net</th></tr></thead>
      <tbody>${entryRows}</tbody>
    </table>`,
  );

  return openReportPrint(html);
}

export async function printCashReport(report, type, options = {}) {
  const withBranch = report
    ? {
        ...report,
        branch_id: report.branch_id ?? options.branchId,
        all_branches: options.allBranches,
      }
    : report;

  switch (type) {
    case "collection":
      return printCashCollectionReport(withBranch);
    case "x-report":
      return printZReport(withBranch, { ...options, title: "X-Report" });
    case "z-report":
      return printZReport(withBranch, { ...options, title: "Z-Report" });
    case "day-book":
      return printSalesDayBook(withBranch);
    default:
      return false;
  }
}
